import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    CalendarCheck,
    CalendarPlus,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    Home,
    Phone,
    RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const WEEK_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
const isoKey = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmtTime = (iso, tbc = false) => {
    try {
        if (tbc) return "TBC";
        return new Date(iso).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
};

const Calendar = () => {
    const [cursor, setCursor] = useState(new Date());
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [syncing, setSyncing] = useState(false);
    // Book job dialog
    const [bookOpen, setBookOpen] = useState(false);
    const [bookDate, setBookDate] = useState("");
    const [bookMode, setBookMode] = useState("existing"); // "existing" | "new"
    const [customers, setCustomers] = useState([]);
    const [pickedCustomerId, setPickedCustomerId] = useState("");
    const [pickedPropertyId, setPickedPropertyId] = useState("");
    const [customerProps, setCustomerProps] = useState([]);
    const [newName, setNewName] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [newMobile, setNewMobile] = useState("");
    const [newAddress, setNewAddress] = useState("");
    const [bookTime, setBookTime] = useState("");
    const [bookTBC, setBookTBC] = useState(false);
    const [bookNotes, setBookNotes] = useState("");
    const [bookSaving, setBookSaving] = useState(false);
    // Mark done + invoice dialog
    const [doneOpen, setDoneOpen] = useState(false);
    const [doneAppt, setDoneAppt] = useState(null);
    const [doneAmount, setDoneAmount] = useState("");
    const [doneDesc, setDoneDesc] = useState("");
    const [doneNotes, setDoneNotes] = useState("");
    const [doneSaving, setDoneSaving] = useState(false);

    const load = () => {
        setLoading(true);
        api
            .get("/appointments")
            .then((r) => setAppointments(r.data))
            .catch(() => toast.error("Failed to load appointments"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!pickedCustomerId) {
            setCustomerProps([]);
            setPickedPropertyId("");
            return;
        }
        api
            .get("/properties", { params: { customer_id: pickedCustomerId } })
            .then((r) => {
                setCustomerProps(r.data);
                if (r.data.length === 1) setPickedPropertyId(r.data[0].id);
                else setPickedPropertyId("");
            })
            .catch(() => setCustomerProps([]));
    }, [pickedCustomerId]);

    const openBookDialog = (dateObj) => {
        setBookDate(isoKey(dateObj));
        setBookMode("existing");
        setPickedCustomerId("");
        setPickedPropertyId("");
        setNewName("");
        setNewEmail("");
        setNewMobile("");
        setNewAddress("");
        setBookTime("");
        setBookTBC(false);
        setBookNotes("");
        setBookOpen(true);
    };

    const saveBook = async (e) => {
        e.preventDefault();
        setBookSaving(true);
        try {
            const appointment_date =
                bookTBC || !bookTime ? bookDate : `${bookDate}T${bookTime}:00`;
            const payload = {
                appointment_date,
                time_tbc: bookTBC || !bookTime,
                notes: bookNotes.trim() || null,
            };
            if (bookMode === "existing") {
                if (!pickedCustomerId) {
                    toast.error("Pick a customer");
                    setBookSaving(false);
                    return;
                }
                payload.customer_id = pickedCustomerId;
                if (pickedPropertyId) payload.property_id = pickedPropertyId;
            } else {
                if (!newName.trim() || !newAddress.trim()) {
                    toast.error("Name and address required");
                    setBookSaving(false);
                    return;
                }
                payload.new_customer_name = newName.trim();
                payload.new_customer_email = newEmail.trim() || null;
                payload.new_customer_mobile = newMobile.trim() || null;
                payload.new_property_address = newAddress.trim();
            }
            await api.post("/jobs/quick", payload);
            toast.success("Job booked in");
            setBookOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBookSaving(false);
        }
    };

    const syncNow = async () => {
        setSyncing(true);
        try {
            const r = await api.post("/google/sync-now");
            if (r.data.ok === false) {
                toast.error(
                    r.data.reason === "not_connected"
                        ? "Google Calendar not connected"
                        : `Sync failed (${r.data.reason})`,
                );
            } else {
                const u = r.data.updated?.length || 0;
                const c = r.data.cancelled?.length || 0;
                if (u === 0 && c === 0) {
                    toast.success("Already up to date");
                } else {
                    toast.success(`Synced from Google — ${u} moved, ${c} cancelled`);
                    load();
                }
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sync failed");
        } finally {
            setSyncing(false);
        }
    };

    const openDoneDialog = (appt) => {
        setDoneAppt(appt);
        const primary = (appt.service_price_pence || 0) / 100;
        const extras = (appt.line_items || []).reduce(
            (n, li) => n + (li.total_pence || 0) / 100,
            0,
        );
        const total = primary + extras;
        setDoneAmount(total > 0 ? total.toFixed(2) : "");
        setDoneDesc(appt.service_name || "Annual boiler service");
        setDoneNotes("");
        setDoneOpen(true);
    };

    const sendInvoiceAndComplete = async (e) => {
        e.preventDefault();
        const amount = parseFloat(doneAmount);
        if (!amount || amount <= 0) {
            toast.error("Enter an amount");
            return;
        }
        setDoneSaving(true);
        try {
            const primaryTotal =
                amount -
                (doneAppt.line_items || []).reduce(
                    (n, li) => n + (li.total_pence || 0) / 100,
                    0,
                );
            const lines = [];
            if (primaryTotal > 0.005 || (doneAppt.line_items || []).length === 0) {
                lines.push({
                    name: doneDesc.trim() || doneAppt.service_name || "Service",
                    quantity: 1,
                    unit_price_gbp: primaryTotal > 0 ? primaryTotal : amount,
                });
            }
            for (const li of doneAppt.line_items || []) {
                lines.push({
                    name: li.name,
                    quantity: li.quantity ?? 1,
                    unit_price_gbp: (li.unit_price_pence || 0) / 100,
                });
            }
            await api.post(`/jobs/${doneAppt.id}/send-invoice`, {
                line_items: lines,
                notes: doneNotes.trim() || null,
                mark_job_complete: true,
            });
            toast.success("Invoice sent · job marked complete");
            setDoneOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        } finally {
            setDoneSaving(false);
        }
    };

    const skipInvoiceAndComplete = async () => {
        if (!doneAppt) return;
        setDoneSaving(true);
        try {
            await api.post(`/jobs/${doneAppt.id}/complete`);
            toast.success("Job marked complete");
            setDoneOpen(false);
            load();
        } catch {
            toast.error("Could not mark complete");
        } finally {
            setDoneSaving(false);
        }
    };

    // Map ISO-date → array of appointments on that day
    const byDay = useMemo(() => {
        const m = {};
        for (const a of appointments) {
            if (!a.appointment_date) continue;
            const d = new Date(a.appointment_date);
            const k = isoKey(d);
            if (!m[k]) m[k] = [];
            m[k].push({ ...a, _dt: d });
        }
        // Sort each day's list by time
        Object.values(m).forEach((arr) => arr.sort((a, b) => a._dt - b._dt));
        return m;
    }, [appointments]);

    // Build grid: 6 weeks × 7 days = 42 cells, starting on Monday
    const grid = useMemo(() => {
        const first = startOfMonth(cursor);
        // JS getDay(): 0=Sun..6=Sat. We want Monday first.
        const jsDay = first.getDay();
        const offset = (jsDay + 6) % 7; // Mon=0, ..., Sun=6
        const start = new Date(first);
        start.setDate(first.getDate() - offset);
        const cells = [];
        for (let i = 0; i < 42; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            cells.push(d);
        }
        return cells;
    }, [cursor]);

    const selectedList = byDay[isoKey(selectedDate)] || [];
    const monthLabel = cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    const today = new Date();

    return (
        <div className="space-y-6" data-testid="calendar-page">
            <div className="flex flex-wrap gap-3 items-end justify-between">
                <div>
                    <p className="text-label">Schedule</p>
                    <h2 className="font-display font-black text-4xl uppercase tracking-tight leading-none">
                        Calendar
                    </h2>
                    <p className="text-neutral-600 mt-2">
                        Every booked appointment on one page. Click a day for the run-sheet.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={syncNow}
                        disabled={syncing}
                        className="dh-btn"
                        data-variant="ghost"
                        data-testid="cal-sync-now"
                        title="Pull any changes from Google Calendar"
                    >
                        <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
                        <span className="hidden sm:inline">{syncing ? "Syncing…" : "Sync"}</span>
                    </button>
                    <button
                        onClick={() => setCursor(addMonths(cursor, -1))}
                        className="dh-btn"
                        data-variant="ghost"
                        data-testid="cal-prev"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <button
                        onClick={() => {
                            const n = new Date();
                            setCursor(n);
                            setSelectedDate(n);
                        }}
                        className="dh-btn"
                        data-variant="ghost"
                        data-testid="cal-today"
                    >
                        Today
                    </button>
                    <button
                        onClick={() => setCursor(addMonths(cursor, 1))}
                        className="dh-btn"
                        data-variant="ghost"
                        data-testid="cal-next"
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>
            </div>

            <div className="dh-card p-0 overflow-hidden">
                <div className="px-4 py-3 border-b-2 border-[color:var(--dh-line)] flex items-center justify-between">
                    <h3 className="font-display font-black text-2xl uppercase leading-none">
                        {monthLabel}
                    </h3>
                    <p className="text-label">
                        {appointments.length} appointment{appointments.length === 1 ? "" : "s"} in total
                    </p>
                </div>

                <div className="grid grid-cols-7 border-b-2 border-[color:var(--dh-line)]">
                    {WEEK_LABELS.map((w) => (
                        <div
                            key={w}
                            className="text-[10px] tracking-widest uppercase font-bold text-neutral-500 py-2 text-center border-r border-neutral-200 last:border-r-0"
                        >
                            {w}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7">
                    {grid.map((d, idx) => {
                        const key = isoKey(d);
                        const list = byDay[key] || [];
                        const inMonth = d.getMonth() === cursor.getMonth();
                        const isToday = sameDay(d, today);
                        const isSelected = sameDay(d, selectedDate);
                        return (
                            <button
                                key={idx}
                                onClick={() => setSelectedDate(new Date(d))}
                                data-testid={`cal-day-${key}`}
                                className={`min-h-[92px] p-2 text-left border-r border-b border-neutral-200 last:border-r-0 relative transition-colors ${
                                    !inMonth ? "bg-neutral-50 text-neutral-400" : "bg-white"
                                } ${isSelected ? "outline outline-3 outline-[color:var(--dh-orange)] outline-offset-[-3px]" : ""}`}
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <span
                                        className={`text-xs font-mono font-bold ${
                                            isToday
                                                ? "bg-[color:var(--dh-ink)] text-white px-1.5 py-0.5"
                                                : ""
                                        }`}
                                    >
                                        {d.getDate()}
                                    </span>
                                    {list.length > 0 && (
                                        <span className="text-[9px] tracking-widest uppercase font-bold text-[color:var(--dh-orange)]">
                                            {list.length}
                                        </span>
                                    )}
                                </div>
                                <div className="space-y-0.5">
                                    {list.slice(0, 3).map((a) => (
                                        <div
                                            key={a.id}
                                            className="text-[10px] font-mono leading-tight truncate bg-[color:var(--dh-orange)] text-white px-1 py-0.5"
                                            title={`${a.customer_name} · ${a.property_address}`}
                                        >
                                            {fmtTime(a.appointment_date, a.appointment_time_tbc)} {a.customer_name}
                                        </div>
                                    ))}
                                    {list.length > 3 && (
                                        <div className="text-[10px] font-mono text-neutral-500">
                                            + {list.length - 3} more
                                        </div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="dh-card p-5" data-testid="cal-day-panel">
                <div className="flex items-center gap-2 mb-4 justify-between flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                        <CalendarCheck className="text-[color:var(--dh-orange)]" size={20} />
                        <h3 className="font-display font-black text-2xl uppercase leading-none">
                            {selectedDate.toLocaleDateString("en-GB", {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                            })}
                        </h3>
                    </div>
                    <button
                        className="dh-btn"
                        onClick={() => openBookDialog(selectedDate)}
                        data-testid="cal-add-job-btn"
                    >
                        <CalendarPlus size={16} /> Add job
                    </button>
                </div>

                {loading ? (
                    <p className="text-label">Loading…</p>
                ) : selectedList.length === 0 ? (
                    <p className="text-neutral-600">Nothing booked in for this day.</p>
                ) : (
                    <ul className="space-y-3">
                        {selectedList.map((a) => (
                            <li
                                key={a.id}
                                data-testid={`cal-appt-${a.id}`}
                                className="border-2 border-[color:var(--dh-line)] p-3 flex flex-col sm:flex-row gap-3 sm:items-center bg-white"
                            >
                                <div className="status-green px-3 py-1 text-center sm:min-w-[92px]">
                                    <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">
                                        Arrive
                                    </p>
                                    <p className="font-display font-black text-2xl leading-none">
                                        {fmtTime(a.appointment_date, a.appointment_time_tbc) || "—"}
                                    </p>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-label">{a.service_name || "Service"}</p>
                                    <p className="font-display font-black uppercase text-lg leading-tight">
                                        {a.customer_name}
                                    </p>
                                    <p className="text-sm text-neutral-600 truncate">
                                        {a.property_address}
                                    </p>
                                    <p className="text-xs text-neutral-500 font-mono mt-1">
                                        {a.customer_email || "no email"} ·{" "}
                                        {a.customer_mobile || "no phone"}
                                    </p>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    {a.customer_mobile && (
                                        <a
                                            href={`tel:${a.customer_mobile.replace(/\s/g, "")}`}
                                            className="dh-btn"
                                            data-variant="ghost"
                                            data-testid={`cal-call-${a.id}`}
                                        >
                                            <Phone size={14} /> Call
                                        </a>
                                    )}
                                    <Link
                                        to={`/properties/${a.property_id}`}
                                        className="dh-btn"
                                        data-variant="ghost"
                                        data-testid={`cal-open-${a.id}`}
                                    >
                                        <Home size={14} /> Open
                                    </Link>
                                    <button
                                        className="dh-btn"
                                        data-testid={`cal-done-${a.id}`}
                                        onClick={() => openDoneDialog(a)}
                                    >
                                        <CheckCircle2 size={14} /> Done
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <Dialog open={bookOpen} onOpenChange={setBookOpen}>
                <DialogContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase" data-testid="book-dialog-title">
                            Book a job — {bookDate}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={saveBook} className="space-y-4" data-testid="cal-book-form">
                        <div className="flex gap-2 border-b-2 border-[color:var(--dh-line)]">
                            <button
                                type="button"
                                onClick={() => setBookMode("existing")}
                                data-testid="book-mode-existing"
                                className={`font-display uppercase font-bold tracking-wide px-4 py-2 border-b-4 -mb-[2px] ${
                                    bookMode === "existing"
                                        ? "border-[color:var(--dh-orange)] text-black"
                                        : "border-transparent text-neutral-500 hover:text-black"
                                }`}
                            >
                                Existing customer
                            </button>
                            <button
                                type="button"
                                onClick={() => setBookMode("new")}
                                data-testid="book-mode-new"
                                className={`font-display uppercase font-bold tracking-wide px-4 py-2 border-b-4 -mb-[2px] ${
                                    bookMode === "new"
                                        ? "border-[color:var(--dh-orange)] text-black"
                                        : "border-transparent text-neutral-500 hover:text-black"
                                }`}
                            >
                                New customer
                            </button>
                        </div>

                        {bookMode === "existing" ? (
                            <div className="space-y-3">
                                <div>
                                    <label className="text-label block mb-1">Customer</label>
                                    <Select value={pickedCustomerId} onValueChange={setPickedCustomerId}>
                                        <SelectTrigger data-testid="book-customer-select">
                                            <SelectValue placeholder="Search customers…" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-72">
                                            {customers.map((c) => (
                                                <SelectItem key={c.id} value={c.id} data-testid={`book-customer-${c.id}`}>
                                                    {c.name} {c.email ? `· ${c.email}` : ""}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {customerProps.length > 1 && (
                                    <div>
                                        <label className="text-label block mb-1">Property</label>
                                        <Select value={pickedPropertyId} onValueChange={setPickedPropertyId}>
                                            <SelectTrigger data-testid="book-property-select">
                                                <SelectValue placeholder="Pick a property" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {customerProps.map((p) => (
                                                    <SelectItem key={p.id} value={p.id} data-testid={`book-property-${p.id}`}>
                                                        {p.address}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                {customerProps.length === 1 && (
                                    <p className="text-xs text-neutral-500 font-mono">
                                        Property: {customerProps[0].address}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="grid gap-3">
                                <div>
                                    <label className="text-label block mb-1">Name</label>
                                    <input required className="dh-input" data-testid="book-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-label block mb-1">Email</label>
                                        <input type="email" className="dh-input" data-testid="book-new-email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="text-label block mb-1">Mobile</label>
                                        <input className="dh-input" data-testid="book-new-mobile" value={newMobile} onChange={(e) => setNewMobile(e.target.value)} />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-label block mb-1">Address</label>
                                    <textarea required rows={2} className="dh-input" data-testid="book-new-address" value={newAddress} onChange={(e) => setNewAddress(e.target.value)} />
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-label block mb-1">Date</label>
                                <input
                                    type="date"
                                    required
                                    className="dh-input"
                                    data-testid="book-date"
                                    value={bookDate}
                                    onChange={(e) => setBookDate(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="text-label block mb-1">
                                    Time {bookTBC && <span className="text-neutral-500">(TBC)</span>}
                                </label>
                                <input
                                    type="time"
                                    className="dh-input"
                                    data-testid="book-time"
                                    disabled={bookTBC}
                                    value={bookTime}
                                    onChange={(e) => setBookTime(e.target.value)}
                                />
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                data-testid="book-tbc"
                                checked={bookTBC}
                                onChange={(e) => setBookTBC(e.target.checked)}
                            />
                            <span>Time TBC — I&rsquo;ll confirm later</span>
                        </label>
                        <div>
                            <label className="text-label block mb-1">Notes (optional)</label>
                            <textarea rows={2} className="dh-input" data-testid="book-notes" value={bookNotes} onChange={(e) => setBookNotes(e.target.value)} />
                        </div>

                        <DialogFooter>
                            <button className="dh-btn" data-testid="book-save" disabled={bookSaving}>
                                {bookSaving ? "Saving…" : "Book it in"}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={doneOpen} onOpenChange={setDoneOpen}>
                <DialogContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase">
                            Send invoice &amp; complete job
                        </DialogTitle>
                    </DialogHeader>
                    {doneAppt && (
                        <p className="text-sm text-neutral-600 -mt-2">
                            {doneAppt.customer_name} · {doneAppt.property_address}
                        </p>
                    )}
                    <form onSubmit={sendInvoiceAndComplete} className="space-y-4" data-testid="cal-done-form">
                        <div>
                            <label className="text-label block mb-1">Amount (£)</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                required
                                placeholder="90.00"
                                className="dh-input text-xl font-bold"
                                data-testid="cal-done-amount"
                                value={doneAmount}
                                onChange={(e) => setDoneAmount(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-label block mb-1">Description on invoice</label>
                            <input
                                className="dh-input"
                                data-testid="cal-done-desc"
                                value={doneDesc}
                                onChange={(e) => setDoneDesc(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-label block mb-1">Notes (optional)</label>
                            <textarea
                                rows={2}
                                className="dh-input"
                                data-testid="cal-done-notes"
                                value={doneNotes}
                                onChange={(e) => setDoneNotes(e.target.value)}
                            />
                        </div>
                        <DialogFooter className="flex-wrap gap-2">
                            <button
                                type="button"
                                className="text-xs text-neutral-500 underline"
                                data-testid="cal-done-skip"
                                onClick={skipInvoiceAndComplete}
                                disabled={doneSaving}
                            >
                                Skip invoice, just mark complete
                            </button>
                            <button
                                className="dh-btn"
                                data-testid="cal-done-send"
                                disabled={doneSaving}
                            >
                                {doneSaving ? "Sending…" : "Send invoice"}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Calendar;
